import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  type RentalManagerUserPropertyMultiplexWebApiHandler_update_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_update_Options,
  useRentalManagerUserPropertyMultiplexWebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import type { Client } from '@zg-rentals/http-client';
import type {
  ApiResponseErrorDto,
  MultiplexUpdateFieldErrorWebDto,
  UserPropertyMultiplexUpdateRequestWebDto,
  UserPropertyMultiplexUpdateResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

/**
 * Amenity-related fields on {@link UserPropertyMultiplexUpdateRequestWebDto} used when mapping multi-edit payloads
 * into multiplex `postData`.
 */
export const MULTIPLEX_AMENITY_KEYS = [
  'balconyDeckPatioPorch',
  'hasAttachedGarageParking',
  'hasBicycleStorage',
  'hasDetachedGarageParking',
  'hasDisabledAccess',
  'hasEvCharger',
  'hasSwimmingPool',
  'offStreetParking',
  'isRentersInsuranceRequired',
] as const satisfies ReadonlyArray<keyof UserPropertyMultiplexUpdateRequestWebDto>;

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const collectMultiplexUpdateFieldErrors = (
  responseData: UserPropertyMultiplexUpdateResponseWebDto | undefined,
): Array<MultiplexUpdateFieldErrorWebDto> => {
  if (!responseData?.units?.length) {
    return [];
  }

  return responseData.units.flatMap((unit) => unit.errors ?? []);
};

/**
 * Runtime guard beyond compile-time args:
 * `getParams.multiplexBuildingSessionId` must be a non-empty string; `postData` must be a plain object (not an array).
 */
const assertMultiplexUpdateMutationArgs = (args: RentalManagerUserPropertyMultiplexWebApiHandler_update_Args): void => {
  const { postData, getParams } = args;

  if (typeof postData !== 'object' || postData === null || Array.isArray(postData)) {
    throw new TypeError(
      'useMultiplexUpdate: `postData` must be a non-array object (UserPropertyMultiplexUpdateRequestWebDto).',
    );
  }

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexUpdate: `getParams` is required (UpdateParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexUpdate: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexUpdateOptions = Omit<
  RentalManagerUserPropertyMultiplexWebApiHandler_update_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexUpdateMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexWebApiHandler_update>;

export type UseMultiplexUpdateResult = Omit<
  BaseMultiplexUpdateMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_update_Args,
    options?: Parameters<BaseMultiplexUpdateMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexUpdateMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_update_Args,
    options?: Parameters<BaseMultiplexUpdateMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexUpdateMutation['mutateAsync']>;
  data: BaseMultiplexUpdateMutation['data'];
  error: BaseMultiplexUpdateMutation['error'];
  isPending: BaseMultiplexUpdateMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  fieldErrors: Array<MultiplexUpdateFieldErrorWebDto>;
  success: boolean | undefined;
  units: UserPropertyMultiplexUpdateResponseWebDto['units'] | undefined;
};

/**
 * Multiplex listing update: wraps the generated `update` mutation (PUT `/api/web/v1/users/properties/multiplex/update`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), `fieldErrors` (flattened unit-level errors from the response
 * body), and `success` / `units` from the last successful `data`.
 *
 * **Calling the mutation:** Use `{ getParams: { multiplexBuildingSessionId }, postData }` where `postData` matches
 * `UserPropertyMultiplexUpdateRequestWebDto` (optional listing fields). Compile-time typing applies to `mutate` /
 * `mutateAsync`; runtime checks enforce non-array `postData`, `getParams`, and a non-empty session id string.
 */
const useMultiplexUpdate = (mutationOptions: UseMultiplexUpdateOptions = {}): UseMultiplexUpdateResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexWebApiHandler_update({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexWebApiHandler_update',
        details: 'useMultiplexUpdate',
        ...metaOverrides?.context,
      },
    },
  });

  /*
   * Many mutation wrappers leave data/error/isPending on `...rest` only. We destructure `data` here to derive
   * fieldErrors/success/units, and list error/isPending next to `errors` for a clear return shape. Callers still get the
   * same fields they would from spreading the underlying mutation result.
   */
  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexUpdateMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexUpdateMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    fieldErrors: collectMultiplexUpdateFieldErrors(data),
    success: data?.success,
    units: data?.units,
  };
};

export default useMultiplexUpdate;
